// src/pages/orders/adminOrders.js
// Admin order list backed by the commerce edge (orders query). Staff-gated server-side.
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardBody } from "../../components/card/card.jsx";
import { fetchCommerceOrders } from "../../actions/commerce_admin_actions";

const TABS = [
  { label: "All", value: null },
  { label: "Unfulfilled", value: "UNFULFILLED" },
  { label: "Fulfilled", value: "FULFILLED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Refunded", value: "REFUNDED" },
];

const STATUS_BADGE = {
  unfulfilled: "bg-warning text-dark",
  fulfilled: "bg-success",
  cancelled: "bg-secondary",
  refunded: "bg-info text-dark",
};

function money(m) {
  if (!m || !m.gross) return "—";
  return `${m.gross.currency} ${Number(m.gross.amount).toFixed(2)}`;
}
function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString();
}

export default function AdminOrders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, pageInfo, totalCount, ordersLoading, ordersError } = useSelector((s) => s.commerceAdmin);
  const [status, setStatus] = useState(null);
  const [includeTest, setIncludeTest] = useState(false);

  useEffect(() => {
    dispatch(fetchCommerceOrders({ first: 25, status, includeTest }));
  }, [dispatch, status, includeTest]);

  const loadMore = () => {
    if (pageInfo.hasNextPage) dispatch(fetchCommerceOrders({ first: 25, after: pageInfo.endCursor, status, includeTest }));
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <h1 className="page-header mb-0">Manage Orders</h1>
          <div className="text-inverse text-opacity-50">Orders served by the commerce engine</div>
        </div>
      </div>

      <Card>
        <CardBody>
          <ul className="nav nav-tabs nav-tabs-v2 mb-3">
            {TABS.map((t) => (
              <li className="nav-item me-2" key={t.label}>
                <button
                  className={"nav-link " + (status === t.value ? "active" : "")}
                  onClick={() => setStatus(t.value)}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Test orders are hidden by default so this list reads as real
              business. This is how you get at a rehearsal order when you need to
              inspect one — they are tagged, not deleted, precisely so they stay
              reachable. */}
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="includeTestOrders"
              checked={includeTest}
              onChange={(e) => setIncludeTest(e.target.checked)}
            />
            <label className="form-check-label small text-inverse text-opacity-75" htmlFor="includeTestOrders">
              Show test orders
              <span className="text-inverse text-opacity-50">
                {" "}— tagged is_test; excluded from revenue, allocate no stock
              </span>
            </label>
          </div>

          {ordersError ? (
            <div className="alert alert-danger">Could not load orders: {ordersError}</div>
          ) : null}

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Source</th>
                  <th>Channel</th>
                  <th className="text-end">Total</th>
                  <th>Status</th>
                  <th>Payment</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/orders/manage/${encodeURIComponent(o.id)}`)}
                  >
                    <td className="fw-bold">#{o.number}</td>
                    <td>{when(o.created)}</td>
                    <td>{o.userEmail || "—"}</td>
                    <td><span className="badge bg-indigo bg-opacity-25 text-indigo">{o.source || "—"}</span></td>
                    <td>{o.channel ? o.channel.slug : "—"}</td>
                    <td className="text-end">{money(o.total)}</td>
                    <td>
                      <span className={"badge " + (STATUS_BADGE[o.status] || "bg-secondary")}>{o.status}</span>
                    </td>
                    <td>{o.paymentStatusDisplay || "—"}</td>
                  </tr>
                ))}
                {!ordersLoading && orders.length === 0 ? (
                  <tr><td colSpan="8" className="text-center text-inverse text-opacity-50 py-4">No orders found.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {ordersLoading ? <div className="text-center py-3">Loading…</div> : null}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-inverse text-opacity-50 small">
              Showing {orders.length}{totalCount ? ` of ${totalCount}+` : ""}
            </div>
            <button className="btn btn-outline-theme btn-sm" disabled={!pageInfo.hasNextPage || ordersLoading} onClick={loadMore}>
              {pageInfo.hasNextPage ? "Load more" : "End of list"}
            </button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
